import { useMemo, useState } from 'react';
import { Table, Tag, Input, Button } from 'antd';
import dayjs from 'dayjs';
import { usePatientStore } from '../../store';
import DataFetchManager from '../DataFetchManager';
import { parseFieldSetup } from '../../utils/stringUtils';
import { ReloadOutlined } from '@ant-design/icons';

const getColumns = (
  hasMatchedCall,
  statusFilters,
  defaultStatusFilteredValue,
  siteFilters,
  clientFilters
) => [
  {
    title: 'Scheduled',
    key: 'scheduleDateTime',
    width: 120,
    fixed: 'left',
    render: (_, record) => (
      <span>
        {record?.scheduleDateTime
          ? dayjs(record?.scheduleDateTime).utc().format('DD/MM HH:mm')
          : ''}
      </span>
    ),
    defaultSortOrder: 'ascend',
    sorter: (a, b) =>
      dayjs(a.createdDateTime).unix() - dayjs(b.createdDateTime).unix(),
  },
  {
    title: 'Patient',
    key: 'patientFullName',
    width: 150,
    fixed: 'left',
    render: (_, record) => (
      <span>
        {`${record?.patientfirstName ? parseFieldSetup(record.patientfirstName) : ''}${record?.patientlastName ? ' ' + parseFieldSetup(record.patientlastName) : ''}`}
      </span>
    ),
    ellipsis: true,
  },
  {
    title: 'DOB',
    key: 'dob',
    width: 90,
    render: (_, record) => (
      <span>
        {record?.patientdob
          ? dayjs(record?.patientdob).utc().format('DD/MM/YY')
          : ''}
      </span>
    ),
  },
  {
    title: 'Site',
    key: 'sitecode',
    dataIndex: 'sitecode',
    width: 80,
    ellipsis: true,
    filters: siteFilters,
    filterSearch: true,
    onFilter: (value, record) =>
      (record.sitecode || '').toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: 'Status',
    key: 'status',
    dataIndex: 'status',
    width: 120,
    ellipsis: true,
    filters: statusFilters,
    defaultFilteredValue: defaultStatusFilteredValue,
    onFilter: (value, record) =>
      (record.status || '').toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: 'Urgency',
    key: 'urgent',
    width: 70,
    render: (_, record) => (
      <span>
        {record?.urgent === 2 ? (
          <Tag color="red" size="small">
            Urgent
          </Tag>
        ) : (
          record?.urgent === 1 && (
            <Tag color="green" size="small">
              Normal
            </Tag>
          )
        )}
      </span>
    ),
  },
  {
    title: 'Phone',
    key: 'phone',
    dataIndex: 'patientmobilePhone',
    width: 110,
    ellipsis: true,
  },
  {
    title: 'Client',
    key: 'client',
    dataIndex: 'client',
    width: 80,
    ellipsis: true,
    filters: clientFilters,
    filterSearch: true,
    onFilter: (value, record) =>
      (record.client || '').toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: 'Created',
    key: 'createdDateTime',
    width: 90,
    render: (_, record) => (
      <span>
        {record?.createdDateTime
          ? dayjs(record?.createdDateTime).utc().format('DD/MM HH:mm')
          : ''}
      </span>
    ),
  },
  {
    title: 'Notes',
    key: 'apptNotes',
    dataIndex: 'appointmentnote',
    ellipsis: true,
    width: 200,
  },
];

const AppointmentTable = ({ matchedPairs = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Helper function to check if appointment has matched Active Call
  const hasMatchedCall = (appointment) => {
    if (!matchedPairs?.length) return false;
    return matchedPairs.some(
      (pair) => pair.appointment.appointmentguid === appointment.appointmentguid
    );
  };

  // Helper function to get matched Active Call for an appointment
  const getMatchedActiveCall = (appointment) => {
    if (!matchedPairs?.length) return null;

    const match = matchedPairs.find(
      (pair) => pair.appointment.appointmentguid === appointment.appointmentguid
    );

    return match ? match.activeCall : null;
  };
  const {
    fetchMRAppointments,
    isFetchingMRAppointments,
    patientMRAppointments,
  } = usePatientStore((state) => ({
    fetchMRAppointments: state.fetchMRAppointments,
    isFetchingMRAppointments: state.isFetchingMRAppointments,
    patientMRAppointments: state.patientMRAppointments,
  }));

  const statusFilters = useMemo(() => {
    if (!patientMRAppointments?.length) return [];
    const uniqueStatuses = [
      ...new Set(
        patientMRAppointments
          .map((appointment) => appointment.status?.trim())
          .filter((status) => status && status.length > 0)
      ),
    ].sort();

    return uniqueStatuses.map((status) => ({
      text: status,
      value: status.toLowerCase(),
    }));
  }, [patientMRAppointments]);

  const siteFilters = useMemo(() => {
    if (!patientMRAppointments?.length) return [];
    const uniqueSites = [
      ...new Set(
        patientMRAppointments
          .map((appointment) => appointment.sitecode?.trim())
          .filter((site) => site && site.length > 0)
      ),
    ].sort();

    return uniqueSites.map((site) => ({
      text: site,
      value: site.toLowerCase(),
    }));
  }, [patientMRAppointments]);

  const clientFilters = useMemo(() => {
    if (!patientMRAppointments?.length) return [];
    const uniqueClients = [
      ...new Set(
        patientMRAppointments
          .map((appointment) => appointment.client?.trim())
          .filter((client) => client && client.length > 0)
      ),
    ].sort();

    return uniqueClients.map((client) => ({
      text: client,
      value: client.toLowerCase(),
    }));
  }, [patientMRAppointments]);

  const defaultStatusFilteredValue = useMemo(() => {
    const allowed = ['at billing', 'waiting room', 'with doctor'];
    return statusFilters.map((f) => f.value).filter((v) => allowed.includes(v));
  }, [statusFilters]);

  const filteredAppointments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return patientMRAppointments;
    return (patientMRAppointments || []).filter((appt) => {
      const first = appt.patientfirstName || '';
      const last = appt.patientlastName || '';
      const name = `${parseFieldSetup(first)} ${parseFieldSetup(
        last
      )}`.toLowerCase();
      const phone = (appt.patientmobilePhone || '').toLowerCase();
      return name.includes(term) || phone.includes(term);
    });
  }, [patientMRAppointments, searchTerm]);

  return (
    <>
      <DataFetchManager
        fetchData={fetchMRAppointments}
        renderData={() => (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <Input.Search
                allowClear
                placeholder="Search patient or mobile"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={(val) => setSearchTerm(val)}
                style={{ maxWidth: 320 }}
              />
              <Button
                style={{ marginRight: 8 }}
                icon={<ReloadOutlined />}
                onClick={fetchMRAppointments}
                loading={isFetchingMRAppointments}
              >
                Refresh
              </Button>
            </div>
            <Table
              bordered
              rowKey={(record) => record.appointmentguid}
              dataSource={filteredAppointments}
              columns={getColumns(
                hasMatchedCall,
                statusFilters,
                defaultStatusFilteredValue,
                siteFilters,
                clientFilters
              )}
              size="small"
              loading={isFetchingMRAppointments}
              scroll={{ x: 1100, y: 'calc(125vh - 580px)' }}
              pagination={{
                pageSize: 20,
                size: 'small',
                position: ['bottomRight'],
                showSizeChanger: false,
              }}
              sticky={{
                offsetHeader: 0,
              }}
              onRow={(record) => ({
                style: {
                  backgroundColor: hasMatchedCall(record)
                    ? '#f6ffed'
                    : 'transparent',
                  borderLeft: hasMatchedCall(record)
                    ? '4px solid #52c41a'
                    : 'none',
                },
              })}
              expandable={{
                expandedRowRender: (record) => {
                  const matchedCall = getMatchedActiveCall(record);
                  if (matchedCall) {
                    return (
                      <div
                        style={{
                          padding: '12px',
                          backgroundColor: '#f0f9f0',
                          border: '1px solid #b7eb8f',
                          borderRadius: '4px',
                          margin: '4px 0',
                        }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          <Tag color="green" icon={<span>📞</span>}>
                            Active Call Connected
                          </Tag>
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '12px',
                            fontSize: '13px',
                          }}
                        >
                          <div>
                            <strong>Agent:</strong>{' '}
                            {matchedCall.agentName || '-'}
                          </div>
                          <div>
                            <strong>Start Time:</strong>{' '}
                            {matchedCall.startTime || '-'}
                          </div>
                          <div>
                            <strong>Call Stage:</strong>{' '}
                            {matchedCall.callStep || '-'}
                          </div>
                          <div>
                            <strong>Status:</strong>{' '}
                            {matchedCall.currentStatus || '-'}
                          </div>
                          <div>
                            <strong>Call Type:</strong>{' '}
                            {matchedCall.callType || '-'}
                          </div>
                          <div>
                            <strong>Urgency:</strong>{' '}
                            {matchedCall.urgency || '-'}
                          </div>
                          <div>
                            <strong>Outcome:</strong>{' '}
                            {matchedCall.outcome || '-'}
                          </div>
                          <div>
                            <strong>Case ID:</strong>{' '}
                            {matchedCall.caseID || '-'}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                },
                rowExpandable: (record) => hasMatchedCall(record),
                defaultExpandAllRows: false,
              }}
            />
          </>
        )}
      />
    </>
  );
};

export default AppointmentTable;
